<template>
  <div>
    <SetupSteps v-if="!setupComplete" />
    <FormCard
      v-model="merchant"
      :create-button-text="submitButtonText"
      @formSubmit="launchConfirmCountryModal($event)">
      <CardForm
        :resource="merchant"
        resource-name="company"
        :section-title="$t('merchantAccount.legalAndFinancialInfo')">
        <RadioControlWrapper
          label="">
          <BoolRadioControl
            v-model="merchant.merchant_type"
            name="company[merchant_type]"
            :label="$t('merchantAccount.company')"
            used-value="Company" />
          <BoolRadioControl
            v-model="merchant.merchant_type"
            name="company[merchant_type]"
            :label="$t('merchantAccount.individual')"
            used-value="Individual" />
        </RadioControlWrapper>
        <TextInput
          v-model="merchant.legal_name"
          name="company[legal_name]"
          :disclaimer="$t('merchantAccount.invoiceDisclaimer')"
          :custom-label="merchantLabel"
          required />
        <ItemSelect
          v-model="merchant.country_id"
          :items="countries"
          name="company[country_id]"
          :label="$t('merchantAccount.location')"
          :shortened="false"
          :disclaimer="$t('merchantAccount.locationDisclaimer')"
          :disabled="setupComplete"
          @change="changeCurrency()" />
        <ItemSelect
          v-model="merchant.base_currency_id"
          :items="listCurrencies"
          name="company[base_currency_id]"
          :label="$t('merchantAccount.defaultCurrency')"
          required
          :shortened="false" />
      </CardForm>

      <div class="card form-card">
        <div class="form-card-header px-i">
          <div class="form-card-header-title">
            <h2 class="form-card-header-title-text">{{ $t("merchantAccount.taxRegTitle") }}</h2>
            <h4 class="form-card-header-title-subtitle">{{ $t("merchantAccount.taxRegSubtitle") }}</h4>
          </div>
        </div>
        <div class="card-body form-card-body p-0">
          <div class="fields-table">
            <div class="fields-table-header px-i">
              <div class="fields-table-header-title col-7">
                {{ $t('merchantAccount.identifier') }}
              </div>
              <div class="fields-table-header-title col-9">
                {{ $t('merchantAccount.value') }}
              </div>
            </div>
            <div class="fields-table-body">
              <div class="fields-table-body-row px-i">
                <div class="fields-table-body-row-items">
                  <div class="fields-table-body-row-items-label col-7">
                    {{ $t('merchantAccount.vat') }}
                  </div>
                  <div class="fields-table-body-row-items-input p-0 col-9">
                    <TextInput v-model="merchant.vat_number"
                               name="company[vat_number]"
                               :display-label="false" />
                  </div>
                </div>
              </div>
              <div class="fields-table-body-row px-i">
                <div class="fields-table-body-row-items">
                  <div class="fields-table-body-row-items-label col-7">
                    {{ $t('merchantAccount.mof') }}
                  </div>
                  <div class="fields-table-body-row-items-input p-0 col-9">
                    <TextInput v-model="merchant.mof_number"
                               name="company[mof_number]"
                               :display-label="false" />
                  </div>
                </div>
              </div>
              <div class="fields-table-body-row px-i">
                <div class="fields-table-body-row-items">
                  <div class="fields-table-body-row-items-label col-7">
                    {{ $t('merchantAccount.cr') }}
                  </div>
                  <div class="fields-table-body-row-items-input p-0 col-9">
                    <TextInput v-model="merchant.cr_number"
                               name="company[cr_number]"
                               :display-label="false" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template>
        <div class="card form-card">
          <div class="form-card-header px-i">
            <div class="form-card-header-title">
              <h2 class="form-card-header-title-text">{{ $t("merchantAccount.taxRateTitle") }}</h2>
              <h4 class="form-card-header-title-subtitle">{{ $t("merchantAccount.taxRateSubtitle") }}</h4>
            </div>
          </div>
          <div class="card-body form-card-body p-0">
            <div class="fields-table">
              <div class="fields-table-header px-i">
                <div class="fields-table-header-title col-11">
                  {{ $t('merchantAccount.taxName') }}
                </div>
                <div class="fields-table-header-title col-9">
                  {{ $t('merchantAccount.rate') }}
                </div>
              </div>
              <div class="fields-table-body">
                <template v-for="(tax, index) in merchantTaxes">
                  <div :key="index" class="fields-table-body-row fields-table-body-row-bordered-last px-i" :class="{ 'd-none': tax.destroy }">
                    <HiddenInput
                      v-model="tax.id"
                      :name="`company[taxes_attributes][${index}][id]`" />
                    <input v-model="tax.destroy" :name="`company[taxes_attributes][${index}][_destroy]`" type="hidden">
                    <div class="fields-table-body-row-items">
                      <div class="fields-table-body-row-items-input col-11">
                        <div class="form-group custom-form-group">
                          <select v-model="tax.name"
                                  :name="`company[taxes_attributes][${index}][name]`"
                                  class="custom-select"
                                  required="true">
                            <option v-for="taxName in Object.keys(taxNames)"
                                    :key="taxName"
                                    :value="taxName">
                              {{ taxNames[taxName] }}
                            </option>
                          </select>
                        </div>
                      </div>
                      <div class="fields-table-body-row-items-input p-0 col-9">
                        <TextInput v-model="tax.rate"
                                   :name="`company[taxes_attributes][${index}][rate]`"
                                   prepend="%"
                                   :display-label="false"
                                   required />
                      </div>
                      <div class="fields-table-body-row-items-action col-4">
                        <a class="fields-table-body-row-items-action-link" href="#" @click="removeTax($event, index)">
                          <i class="demo-icon icon-times" />
                        </a>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
              <div class="fields-table-footer px-i">
                <button type="button" class="btn btn-primary ihjoz-button" @click="addTaxClicked($event)">
                  {{ $t('common.add') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>

      <CardForm
        :resource="merchant"
        resource-name="company"
        :section-title="$t('merchantAccount.primaryContact')"
        :subtitle="$t('merchantAccount.primaryContactText')">
        <TextInput
          v-model="merchant.merchant_name"
          :custom-label="$t('common.name')"
          name="company[merchant_name]"
          required />
        <TextInput
          v-model="merchant.merchant_email"
          :custom-label="$t('common.email')"
          name="company[merchant_email]"
          required />
        <div class="form-group custom-form-group mb-0">
          <label for="mobileInput" class="custom-form-group-label">{{ $t('common.mobile') }}</label>
          <vue-tel-input
            ref="mobileInput"
            v-model="merchant.merchant_mobile"
            input-id="mobileInput"
            name="company[merchant_mobile]"
            :ignored-countries="['IL']"
            :enabled-country-code="true"
            :preferred-countries="['LB', 'SA']"
            :valid-characters-only="true"
            :default-country="defaultCountryCode"
            :disabled-fetching-country="true"
            wrapper-classes="ihjoz-tel-input"
            mode="national"
            input-classes="ihjoz-tel-input-field ihjoz-tel-input-field-code-visible"
            required
            @onInput="onInput"
            @input="validateNumber" />
          <input
            :value="selectedPhoneCountryCode"
            name="company[mobile_country_code]"
            type="hidden">
        </div>
        <MultilineTextInput
          v-model="merchant.merchant_address"
          :custom-label="$t('common.address')"
          name="company[merchant_address]"
          @blur="locateAddress()" />
        <div class="form-card-group">
          <div class="form-card-group-item col-24 col-md-9">
            <ItemSelect
              v-model="merchant.merchant_country"
              :items="countries"
              name="company[merchant_country]"
              :label="$t('common.country')"
              value-method="name"
              :shortened="false"
              @blur="locateAddress()" />
          </div>
          <div class="form-card-group-item col-24 col-md-9">
            <TextInput
              v-model="merchant.merchant_city"
              :custom-label="$t('common.city')"
              name="company[merchant_city]"
              @blur="locateAddress()" />
          </div>
          <div class="form-card-group-item col-24 col-md-6">
            <TextInput
              v-model="merchant.merchant_zip"
              :custom-label="$t('common.zipCode')"
              name="company[merchant_zip]"
              @blur="locateAddress()" />
          </div>
        </div>
        <HiddenInput
          v-model="merchant.merchant_latitude"
          name="company[merchant_latitude]" />
        <HiddenInput
          v-model="merchant.merchant_longitude"
          name="company[merchant_longitude]" />
        <div class="form-group custom-form-group">
          <label class="custom-form-group-label">
            {{ $t('common.map') }}
          </label>
          <div class="custom-form-group-radio custom-form-group-radio-small-column">
            <div class="custom-radio-button">
              <input id="company-map-setting-0"
                     v-model="merchant.merchant_map_setting"
                     class="custom-radio-button-input"
                     type="radio"
                     name="company[merchant_map_setting]"
                     value="0">
              <label for="company-map-setting-0" class="custom-radio-button-label">
                <span>{{ $t('common.locateAutomatically') }}</span>
              </label>
            </div>
            <div class="custom-radio-button">
              <input id="company-map-setting-1"
                     v-model="merchant.merchant_map_setting"
                     class="custom-radio-button-input"
                     type="radio"
                     name="company[merchant_map_setting]"
                     value="1">
              <label for="company-map-setting-1" class="custom-radio-button-label">
                <span>{{ $t('common.pinOnMap') }}</span>
              </label>
            </div>
            <div class="custom-radio-button">
              <input id="company-map-setting-2"
                     v-model="merchant.merchant_map_setting"
                     class="custom-radio-button-input"
                     type="radio"
                     name="company[merchant_map_setting]"
                     value="2">
              <label for="company-map-setting-2" class="custom-radio-button-label">
                <span>{{ $t('common.hide') }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="form-card-map custom-form-group" :class="{ 'mb-n4': !isMapVisible }">
          <div v-show="isMapVisible" id="google-map" style="height: 400px;" />
        </div>
      </CardForm>
      <CardForm
        v-if="false"
        :resource="merchant"
        resource-name="company"
        :section-title="$t('merchantAccount.settlementDetails')"
        :subtitle="$t('merchantAccount.settlementSubtitle')">
        <RadioControlWrapper
          :label="$t('merchantAccount.preferredSettlementMethod')">
          <BoolRadioControl
            v-model="merchant.preferred_settlement_method"
            name="company[preferred_settlement_method]"
            :label="$t('merchantAccount.bankTransfer')"
            used-value="Bank Transfer" />
          <BoolRadioControl
            v-model="merchant.preferred_settlement_method"
            name="company[preferred_settlement_method]"
            :label="$t('merchantAccount.check')"
            used-value="Check" />
        </RadioControlWrapper>

        <TextInput
          v-model="merchant.bank_number"
          name="company[bank_number]"
          :placeholder="$t('merchantAccount.iban')"
          :custom-label="$t('merchantAccount.bankNumber')" />
      </CardForm>
    </FormCard>
    <div v-if="!setupComplete" id="confirmCountry" class="modal" tabindex="-1" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-body">
            <p>{{ $t('merchantAccount.selectedCountryNotice', { country: selectedCountry}) }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">{{ $t('common.cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="submitForm($event)">{{ $t('common.save') }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { NameOptions } from "../../../dashboard/stores/TaxStore";
import FormCard from "../../../shared/components/UI/FormCard.vue";
import CardForm from "../../../shared/components/UI/CardForm.vue";
import TextInput from "../../../shared/components/UI/TextInput.vue";
import MultilineTextInput from "../UI/MultilineTextInput.vue";
import ItemSelect from "../../../shared/components/UI/ItemSelect.vue";
import RadioControlWrapper from "../../../shared/components/UI/RadioControlWrapper.vue";
import BoolRadioControl from "../../../shared/components/UI/BoolRadioControl.vue";
import { VueTelInput } from "vue-tel-input";
import UserStore from "../../../user_dashboard/components/User/UserStore";
import SetupStore from "../../../shared/components/Setup/SetupStore";
import SetupSteps from "../Setup/SetupSteps.vue";
import TenantStore from "../../../tenant_admin/stores/TenantStore";

export default {
  name: "MerchantAccountForm",

  components: {
    FormCard,
    CardForm,
    TextInput,
    MultilineTextInput,
    ItemSelect,
    RadioControlWrapper,
    BoolRadioControl,
    VueTelInput,
    SetupSteps
  },

  props: ["company", "countries", "currencyTenants", "currencies"],

  data: function () {
    return {
      merchant: this.company,
      userStoreState: UserStore.state,
      setupStoreState: SetupStore.state,
      listCurrencies: this.currencies,
      listCurrencyTenants: this.currencyTenants,
      defaultCountryCode: Dashboard.defaultCountryInfo.alpha2,
      selectedPhoneCountryCode: Dashboard.defaultCountryInfo.dialCode,
      googleMap: null,
      googleMapMarker: null
    };
  },

  computed: {
    userIsMerchant () {
      return this.user.id === this.merchant.user_id;
    },

    selectedCountry: {
      get: function () {
        return this.countries.find(country => country.id.toString() === this.merchant.country_id.toString()).name;
      },

      set: function (newValue) {
        this.merchant.country_id = newValue;
      }
    },

    selectedCurrency () {
      var currency = this.listCurrencies.find(currency => currency.id === this.merchant.base_currency_id);
      if (currency) {
        return currency.name;
      }
    },

    merchantLabel () {
      if (this.merchant.merchant_type === "Company") {
        return this.$t("merchantAccount.legalName");
      } else {
        return this.$t("merchantAccount.fullName");
      }
    },

    user () {
      return this.userStoreState.user;
    },

    userFullName () {
      return `${this.user.first_name} ${this.user.last_name}`;
    },

    taxNames () {
      return NameOptions;
    },

    merchantTaxes () {
      return this.merchant.taxes || [];
    },

    taxNamesKeys () {
      return Object.keys(this.taxNames);
    },

    isMapVisible () {
      return +this.merchant.merchant_map_setting !== Dashboard.mapSettings.dontShowMap;
    },

    hasAddressInfo () {
      return this.merchant.merchant_address || this.merchant.merchant_city || this.merchant.merchant_country;
    },

    setupComplete () {
      return this.setupStoreState.stage === 4;
    },

    submitButtonText () {
      if (this.setupComplete) {
        return "";
      } else {
        return this.$t("common.next");
      }
    }
  },

  watch: {
    "company.merchant_map_setting": function () { this.showMap(); }
  },

  async beforeMount () {
    this.initMerchantInfo();
  },

  mounted () {
    this.initializeMap();
    this.showMap();
    this.initializeCountryCodes();
  },

  methods: {
    launchConfirmCountryModal (e) {
      e.preventDefault();
      this.setupComplete
        ? this.$emit("formSubmit", e)
        : $("#confirmCountry").modal("show");
    },

    submitForm (e) {
      $("#confirmCountry").modal("hide");
      e.preventDefault();
      this.$emit("formSubmit", e);
    },

    initMerchantInfo () {
      if (this.userIsMerchant) {
        this.merchant.merchant_map_setting = this.merchant.merchant_map_setting || 0;
        this.merchant.merchant_name = this.merchant.merchant_name || this.userFullName;
        this.merchant.merchant_email = this.merchant.merchant_email || this.merchantUserEmail();
        this.merchant.merchant_mobile = this.merchant.merchant_mobile || this.merchantUserMobile();
      }
    },

    initializeCountryCodes () {
      this.selectedPhoneCountryCode = this.$refs.mobileInput.activeCountry.dialCode;
    },

    merchantUserEmail () {
      return this.user.user_emails[0].email ? this.user.user_emails[0].email : "";
    },

    merchantUserMobile () {
      return (this.user.user_mobile_numbers[0] && this.user.user_mobile_numbers[0].mobile_number) ? this.user.user_mobile_numbers[0].mobile_number : "";
    },

    async changeCurrency () {
      this.listCurrencyTenants = await TenantStore.methods.fetchVisibleCurrencyTenants(Dashboard.currentTenant.id, this.merchant.country_id);
      return this.listCurrencies = this.listCurrencyTenants.map(currency_tenant => currency_tenant.currency);
    },

    onInput ({ number, isValid, country }) {
      this.selectedPhoneCountryCode = country.dialCode;
    },

    initializeMap () {
      this.googleMap = new google.maps.Map(document.getElementById("google-map"), {
        zoom: 18,
        scrollwheel: false,
        mapTypeId: google.maps.MapTypeId.ROADMAP
      });

      this.googleMapMarker = new google.maps.Marker({
        position: this.googleMap.getCenter(),
        map: this.googleMap,
        draggable: false
      });
    },

    showMap () {
      if (this.isLocateAddress()) {
        this.$nextTick(function () {
          this.locateAddress();
        });
      }
      if (this.isPinOnMap()) {
        this.$nextTick(function () {
          this.pinOnMap();
        });
      }
    },

    addTaxClicked (e) {
      this.addTax();
      e.preventDefault();
    },

    addTax () {
      this.merchantTaxes.push({
        id: null,
        rate: null,
        name: "",
        destroy: false
      });
    },

    removeTax (e, taxIndex) {
      e.preventDefault();
      if (this.merchantTaxes[taxIndex].id) {
        this.$set(this.merchantTaxes[taxIndex], "destroy", true);
      } else {
        this.$delete(this.merchantTaxes, taxIndex);
      }
    },

    isLocateAddress () {
      return +this.merchant.merchant_map_setting === Dashboard.mapSettings.suggestFromAddress;
    },

    locateAddress () {
      const map = this.googleMap;
      const marker = this.googleMapMarker;
      marker.setDraggable(false);

      if (this.hasAddressInfo) {
        new google.maps.Geocoder().geocode({
          address: this.buildAddress()
        },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK) {
            map.setCenter(results[0].geometry.location);
            map.setZoom(18);
            marker.setPosition(map.getCenter());

            this.merchant.merchant_latitude = marker.position.lat();
            this.merchant.merchant_longitude = marker.position.lng();
          } else {
            map.setCenter(new google.maps.LatLng(0, 0));
            map.setZoom(5);

            marker.setPosition(map.getCenter());
          }
        }
        );
      } else {
        map.setCenter(new google.maps.LatLng(0, 0));
        map.setZoom(5);

        marker.setPosition(map.getCenter());
      }
    },

    buildAddress () {
      return `${this.merchant.name}, ${this.merchant.merchant_address}, ${this.merchant.merchant_city}, ${this.merchant.merchant_country}`;
    },

    isPinOnMap () {
      return this.merchant.merchant_map_setting == Dashboard.mapSettings.pinOnMap;
    },

    pinOnMap () {
      const mapCenter = {
        lat: this.merchant.latitude || 33.8938,
        lng: this.merchant.longitude || 35.5018
      };

      const map = this.googleMap;
      const marker = this.googleMapMarker;

      map.setCenter(mapCenter);
      map.setZoom(18);
      marker.setDraggable(true);
      marker.setPosition(map.getCenter());

      google.maps.event.addListener(marker, "dragend", () => {
        this.merchant.merchant_latitude = marker.position.lat();
        this.merchant.merchant_longitude = marker.position.lng();
      });
    },

    validateNumber (formattedNumber, { number, valid, country }) {
      if (number.input && !valid) {
        document.getElementById("mobileInput").setCustomValidity("Please enter a valid mobile number.");
      } else {
        document.getElementById("mobileInput").setCustomValidity("");
      }
    }
  }
};
</script>
